import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { remitosApi } from '../api/remitos'
import ModalListaClientes from '../components/ModalListaClientes'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import Boton from '../components/Boton'
import { hoyISO } from '../utils/fecha'
import { etiquetaCliente } from '../utils/cliente'
import { PROVINCIAS, esCapitalFederal, matchProvincia } from '../data/argentina'
import CampoLocalidad from '../components/CampoLocalidad'
import CampoDireccion from '../components/CampoDireccion'
import { colors, text, space, radius, sx } from '../design/tokens'

const DIR_VACIA = { receptor_nombre: '', telefono: '', calle: '', localidad: '', provincia: '', codigo_postal: '' }

/**
 * Remito libre: alta de un remito SIN NV (traslado, reposición, entrega sin
 * venta cargada). Cliente → T90 (con dirección de entrega) o T80 (multi-
 * destinatario). Al guardar se manda a Presea; con la vuelta del número el
 * remito autoconfirma y entra a logística como cualquier otro.
 */
export default function RemitoFormPage() {
  const navigate = useNavigate()

  const [cliente, setCliente] = useState(null)
  const [transporte, setTransporte] = useState(90)
  const [items, setItems] = useState([])          // [{ articulo_id, descripcion, codigo, cantidad }]
  const [usarDatosCliente, setUsarDatosCliente] = useState(true)  // T90: entregar al domicilio del cliente
  const [dir, setDir] = useState(DIR_VACIA)
  const [observaciones, setObservaciones] = useState('')

  const [pickerCliente, setPickerCliente] = useState(false)
  const [pickerArt, setPickerArt] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const es90 = transporte === 90
  // Provincia/localidad canónicas para que el <select> muestre el valor aunque
  // venga en mayúsculas/sin tildes (#24). No cambia cómo se envía al backend.
  const provinciaCanon = matchProvincia(dir.provincia)
  const esCaba = esCapitalFederal(provinciaCanon)

  const setDirCampo = (k, v) => setDir(d => ({ ...d, [k]: v }))

  // El selector único devuelve [{articulo, cantidad}].
  const agregarArticulos = (seleccion) => {
    setItems(prev => {
      const existentes = new Set(prev.map(i => i.articulo_id))
      const nuevos = seleccion
        .filter(({ articulo: a }) => !existentes.has(a.id))
        .map(({ articulo: a, cantidad }) => ({ articulo_id: a.id, descripcion: a.descripcion, codigo: a.codigo_alfa || a.codigo_presea || '—', cantidad: cantidad || 1 }))
      return [...prev, ...nuevos]
    })
    setPickerArt(false)
  }

  const setCantidad = (id, cant) => setItems(prev => prev.map(i => i.articulo_id === id ? { ...i, cantidad: cant } : i))
  const quitar = (id) => setItems(prev => prev.filter(i => i.articulo_id !== id))

  const validar = () => {
    if (!cliente) return 'Elegí un cliente.'
    if (items.length === 0) return 'Agregá al menos un artículo.'
    if (items.some(i => !i.cantidad || Number(i.cantidad) < 1)) return 'Las cantidades deben ser 1 o más.'
    if (es90 && !usarDatosCliente) {
      if (!dir.calle?.trim()) return 'Cargá la dirección de entrega (calle).'
      if (!dir.provincia) return 'Elegí la provincia de la dirección de entrega.'
      if (esCaba && !dir.codigo_postal?.trim()) return 'En CABA el código postal es obligatorio.'
    }
    return ''
  }

  const guardar = async () => {
    const err = validar()
    if (err) { setError(err); return }
    setError('')
    setGuardando(true)
    try {
      // 1) Crear el remito libre (sin NV). Para T90 con dirección propia la
      //    mandamos (queda guardada en el remito); si "usar datos del cliente"
      //    está tildado, no mandamos nada y el sistema entrega al domicilio del
      //    cliente (fallback de zona y vistas).
      const payload = {
        cliente_id: cliente.id,
        transporte,
        fecha_emision: hoyISO(),
        observaciones_raw: observaciones || null,
        articulos: items.map(i => ({ articulo_id: i.articulo_id, cantidad: Number(i.cantidad) })),
      }
      if (es90 && !usarDatosCliente) payload.direccion_envio = dir
      const { data: remito } = await remitosApi.crear(payload)

      // 2) Enviar a Presea (alta de remito). Al volver el número, autoconfirma.
      await remitosApi.generarPresea(remito.id)

      navigate(`/remitos/${remito.id}`)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo crear el remito.')
      setGuardando(false)
    }
  }

  return (
    <div style={st.wrap}>
      <div style={st.headerRow}>
        <div>
          <h1 style={st.title}>Nuevo remito libre</h1>
          <div style={st.sub}>Remito sin nota de venta — traslado, reposición o entrega sin venta cargada.</div>
        </div>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
      </div>

      {error && <div style={st.errorBox}>{error}</div>}

      <div style={st.card}>
        {/* Cliente */}
        <div>
          <label style={st.label}>Cliente</label>
          <div style={st.clienteRow}>
            <Boton variante="secondary" onClick={() => setPickerCliente(true)}>🔍 Buscar cliente (F6)</Boton>
            {cliente && (
              <div style={st.clienteInfo}>
                <strong>{cliente.nombre}</strong>
                <span style={st.meta}>{etiquetaCliente ? etiquetaCliente(cliente) : `Nº ${cliente.codigo_presea || '—'}`}</span>
              </div>
            )}
          </div>
        </div>

        {/* Transporte */}
        <div>
          <label style={st.label}>Tipo de transporte</label>
          <div style={st.radios}>
            <label style={{ ...st.radio, ...(es90 ? st.radioOn : {}) }}>
              <input type="radio" checked={es90} onChange={() => setTransporte(90)} />
              <span><strong>T90</strong> — entrega única (una dirección)</span>
            </label>
            <label style={{ ...st.radio, ...(!es90 ? st.radioOn : {}) }}>
              <input type="radio" checked={!es90} onChange={() => setTransporte(80)} />
              <span><strong>T80</strong> — multi-destinatario (cargás los destinos en la planilla)</span>
            </label>
          </div>
        </div>

        {/* Dirección de entrega (solo T90) */}
        {es90 && (
          <div>
            <label style={st.label}>Dirección de entrega</label>
            <label style={st.checkLine}>
              <input type="checkbox" checked={usarDatosCliente} onChange={e => setUsarDatosCliente(e.target.checked)} />
              <span>Usar los datos del cliente como dirección de entrega{cliente?.domicilio ? ` (${cliente.domicilio}${cliente.localidad ? ', ' + cliente.localidad : ''})` : ''}</span>
            </label>
            {usarDatosCliente ? null : (
            <>
            <div style={st.row2}>
              <div>
                <label style={st.labelSm}>Receptor (nombre)</label>
                <input style={st.input} value={dir.receptor_nombre} onChange={e => setDirCampo('receptor_nombre', e.target.value)} />
              </div>
              <div>
                <label style={st.labelSm}>Teléfono</label>
                <input style={st.input} value={dir.telefono} onChange={e => setDirCampo('telefono', e.target.value)} placeholder="+54 11 1234-5678" />
              </div>
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <label style={st.labelSm}>Calle, número, piso, depto</label>
              <CampoDireccion
                value={dir.calle}
                onChange={v => setDirCampo('calle', v)}
                onPlaceSelected={d => setDir(prev => ({
                  ...prev,
                  // calle_completa conserva el piso/depto tipeado tras la altura.
                  calle: d.calle_completa || d.calle || prev.calle,
                  localidad: d.localidad || prev.localidad,
                  provincia: d.provincia || prev.provincia,
                  codigo_postal: d.codigo_postal || prev.codigo_postal,
                }))}
                inputStyle={st.input}
                placeholder="Calle y número"
              />
            </div>
            <div style={{ ...st.row2, marginTop: '0.5rem' }}>
              <div>
                <label style={st.labelSm}>Provincia</label>
                <select style={st.input} value={provinciaCanon} onChange={e => {
                  const nueva = e.target.value
                  setDir(d => ({ ...d, provincia: nueva, localidad: esCapitalFederal(nueva) ? '' : d.localidad }))
                }}>
                  <option value="">— Provincia —</option>
                  {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {!esCaba && (
                <div>
                  <label style={st.labelSm}>Localidad</label>
                  <CampoLocalidad
                    provincia={provinciaCanon}
                    value={dir.localidad}
                    onChange={v => setDirCampo('localidad', v)}
                    inputStyle={st.input}
                    cp={dir.codigo_postal}
                  />
                </div>
              )}
              <div>
                <label style={st.labelSm}>Código postal{esCaba ? ' *' : ''}</label>
                <input style={st.input} value={dir.codigo_postal} onChange={e => setDirCampo('codigo_postal', e.target.value)} />
              </div>
            </div>
            </>
            )}
          </div>
        )}

        {!es90 && (
          <div style={st.infoT80}>
            En un T80 los destinatarios se cargan después, desde la <strong>planilla de envío</strong>, una vez que Presea confirma el remito.
          </div>
        )}

        {/* Artículos */}
        <div>
          <div style={st.artHeader}>
            <label style={st.label}>Artículos</label>
            <Boton variante="secondary" sm onClick={() => setPickerArt(true)}>+ Agregar artículos</Boton>
          </div>
          {items.length === 0 ? (
            <div style={st.empty}>Todavía no agregaste artículos.</div>
          ) : (
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Código</th>
                  <th style={st.th}>Descripción</th>
                  <th style={{ ...st.th, width: 110 }}>Cantidad</th>
                  <th style={{ ...st.th, width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.articulo_id}>
                    <td style={{ ...st.td, ...sx.mono }}>{i.codigo}</td>
                    <td style={st.td}>{i.descripcion}</td>
                    <td style={st.td}>
                      <input type="number" min="1" style={st.inputSm} value={i.cantidad}
                        onChange={e => setCantidad(i.articulo_id, e.target.value)} />
                    </td>
                    <td style={st.td}>
                      <Boton variante="ghost" sm onClick={() => quitar(i.articulo_id)} title="Quitar">✕</Boton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Observaciones */}
        <div>
          <label style={st.label}>Observaciones (opcional)</label>
          <textarea style={st.textarea} rows={2} value={observaciones} onChange={e => setObservaciones(e.target.value)} />
        </div>

        <div style={st.actions}>
          <Boton variante="ghost" onClick={() => navigate(-1)}>Cancelar</Boton>
          <Boton variante="primary" disabled={guardando} onClick={guardar}>
            {guardando ? 'Creando…' : 'Crear y enviar a Presea'}
          </Boton>
        </div>
      </div>

      {pickerCliente && (
        <ModalListaClientes
          onSelect={c => { setCliente(c); setPickerCliente(false) }}
          onClose={() => setPickerCliente(false)}
        />
      )}
      {pickerArt && (
        <SelectorArticulosModal
          onConfirm={agregarArticulos}
          onClose={() => setPickerArt(false)}
        />
      )}
    </div>
  )
}

const inputBase = {
  width: '100%', padding: '7px 10px', border: `1px solid ${colors.textPlh}`,
  borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', boxSizing: 'border-box',
}

const st = {
  wrap: { maxWidth: 960 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space['3xl'] },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.textStrong },
  sub: { fontSize: text.sec, color: colors.textSec, marginTop: space.xs },
  errorBox: { background: colors.errBg, color: colors.err, padding: `${space.lg}px ${space.xl}px`, borderRadius: radius.sm, marginBottom: space.xl, fontSize: text.body },
  card: { ...sx.card, display: 'flex', flexDirection: 'column', gap: space['2xl'] },
  label: { ...sx.cardTitle, display: 'block', marginBottom: space.md },
  labelSm: { display: 'block', fontWeight: 600, fontSize: text.sec, color: colors.textSec, marginBottom: space.xs },
  input: inputBase,
  inputSm: { ...inputBase, width: 80, padding: '5px 8px', textAlign: 'right' },
  textarea: { ...inputBase, resize: 'vertical' },
  row2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: space.lg },
  clienteRow: { display: 'flex', gap: space.lg, alignItems: 'center', flexWrap: 'wrap' },
  clienteInfo: { background: colors.linkBg, border: `1px solid #bfdbfe`, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px`, fontSize: text.body, color: colors.text, display: 'flex', flexDirection: 'column', gap: 2 },
  meta: { ...sx.mono, fontSize: text.sec, color: colors.textSec },
  checkLine: { display: 'flex', gap: space.md, alignItems: 'center', fontSize: text.sec, color: colors.textSec, marginBottom: space.md, cursor: 'pointer' },
  radios: { display: 'flex', flexDirection: 'column', gap: space.md },
  radio: { display: 'flex', gap: space.md, alignItems: 'center', padding: `${space.md}px ${space.lg}px`, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: text.body, color: colors.text },
  radioOn: { borderColor: colors.link, background: colors.linkBg },
  infoT80: { background: colors.t80Bg, border: `1px solid #ddd6fe`, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px`, fontSize: text.sec, color: colors.t80Text },
  artHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.md },
  empty: { color: colors.textMeta, textAlign: 'center', padding: `${space.xl}px 0`, fontSize: text.body },
  table: { ...sx.table },
  th: { ...sx.th, padding: `${space.md}px ${space.lg}px` },
  td: { ...sx.td, padding: `${space.md}px ${space.lg}px`, borderBottom: `1px solid ${colors.borderSoft}` },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: space.lg, borderTop: `1px solid ${colors.borderSoft}`, paddingTop: space.xl },
}
